import React, { useContext, useEffect, useState, useRef } from "react";
import ReactApexChart from "react-apexcharts";
import { Container, Row, Col, Card, Button } from "react-bootstrap";
import StationsContext from "../context/otogas/stations/stationsContext";
import spinner from "./Layout/spinner";
import userCan from "../config/user_can";
import AuthContext from "../context/auth/authContext";

function Dashboard() {
  const stationsContext = useContext(StationsContext);
  const { dashboard_data, getDashboardData } = stationsContext;

  const authContext = useContext(AuthContext);
  const { user } = authContext;

  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const isFetchingRef = useRef(false);
  const canView = userCan(user, "GET:Sales:GetDashBoardData");

  const fetchData = async () => {
    if (!canView) return;
    if (isFetchingRef.current) return;
    try {
      isFetchingRef.current = true;
      setLoading(true);
      await getDashboardData();
      setLastUpdated(new Date());
    } finally {
      isFetchingRef.current = false;
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canView) {
      setLoading(false);
      return;
    }
    fetchData();
    const intervalId = setInterval(fetchData, 300000); // every 5 minutes
    return () => clearInterval(intervalId);
  }, [canView, user]);

  if (loading && !dashboard_data) {
    return spinner();
  }

  if (!dashboard_data) {
    return <div>No data available</div>;
  }

  const kpiChange = (current, previous) => {
    const change = current - previous;
    if (previous === 0) {
      if (current > 0) return { change: "+∞%", isPositive: true };
      if (current === 0) return { change: "0.00%", isPositive: false };
    }
    const percentageChange = (((current - previous) / previous) * 100).toFixed(2);
    return {
      change: change > 0 ? `+${percentageChange}%` : `${percentageChange}%`,
      isPositive: change > 0,
    };
  };

  const salesChange = kpiChange(
    dashboard_data.thisWeekSoldQuantities || 0,
    dashboard_data.lastWeekSoldQuantities || 0
  );

  const salesSeries = [
    {
      name: "Sales Quantities (L)",
      data: [
        dashboard_data.thisWeekSoldQuantities || 0,
        dashboard_data.lastWeekSoldQuantities || 0,
      ],
    },
  ];

  const salesOptions = {
    chart: { height: 350, type: "line", toolbar: { show: false } },
    stroke: { curve: "smooth", width: 2 },
    title: { text: "Sales Quantities Comparison", style: { color: "#333" } },
    xaxis: { categories: ["This Week", "Last Week"] },
    colors: ["#007BFF"],
  };

  const topStationsData = dashboard_data.topPerformingStations || [];
  const topStationsSeries = [
    {
      name: "Quantity Sold (L)",
      data: topStationsData.map((station) => station.quantitySold || 0),
    },
  ];

  const topStationsOptions = {
    chart: { type: "bar", height: 350, toolbar: { show: false } },
    plotOptions: { bar: { horizontal: false, columnWidth: "30%", borderRadius: 6 } },
    title: { text: "Top Performing Stations", style: { color: "#333" } },
    xaxis: {
      categories: topStationsData.map((station) => station.stationName || "Unknown"),
    },
    colors: topStationsData.map(
      (_, index) =>
        ["#FF6347", "#28B463", "#1E90FF", "#F39C12", "#9B59B6", "#34495E", "#E74C3C", "#8E44AD", "#2ECC71", "#3498DB"][
        index % 10
        ]
    ),
  };

  return (
    <Container fluid>
      {/* Header with refresh text */}
      {/* Header with refresh text */}
      <Row className="align-items-center mb-4 justify-content-center text-center">
        <Col xs="auto">
          <h2 className="fw-bold mb-0">🚀 Real-Time Dashboard</h2>
        </Col>
        <Col xs="auto" className="text-muted small">
          Last updated: {lastUpdated ? lastUpdated.toLocaleTimeString() : "—"}
        </Col>
        <Col xs="auto">
          <Button
            size="sm"
            variant="outline-primary"
            onClick={fetchData}
            disabled={loading || !canView}
          >
            {loading ? "Refreshing..." : "Refresh"}
          </Button>
        </Col>
      </Row>


      {/* KPI cards - all in one row */}
      <Row className="g-4">
        <Col md={2}>
          <Card className="text-center shadow-lg border-0 h-100"
            style={{ background: "linear-gradient(45deg, #007BFF, #1E90FF)", color: "white" }}>
            <Card.Body>
              <i className="fas fa-users fa-2x mb-2"></i>
              <Card.Title>Total Customers</Card.Title>
              <Card.Text className="fs-2 fw-bold">{dashboard_data.totalCustomers}</Card.Text>
            </Card.Body>
          </Card>
        </Col>

        <Col md={2}>
          <Card className="text-center shadow-lg border-0 h-100"
            style={{ background: "linear-gradient(45deg, #28B463, #2ECC71)", color: "white" }}>
            <Card.Body>
              <i className="fas fa-car fa-2x mb-2"></i>
              <Card.Title>Total Vehicles</Card.Title>
              <Card.Text className="fs-2 fw-bold">{dashboard_data.totalVehicles}</Card.Text>
            </Card.Body>
          </Card>
        </Col>

        <Col md={2}>
          <Card className="text-center shadow-lg border-0 h-100"
            style={{ background: "linear-gradient(45deg, #FF6347, #FF9471)", color: "white" }}>
            <Card.Body>
              <i className="fas fa-chart-line fa-2x mb-2"></i>
              <Card.Title>Sales Today</Card.Title>
              <Card.Text className="fs-2 fw-bold">{dashboard_data.salesToday} L</Card.Text>
            </Card.Body>
          </Card>
        </Col>

        <Col md={2}>
          <Card className="text-center shadow-lg border-0 h-100"
            style={{ background: "linear-gradient(45deg, #546E7A, #6C757D)", color: "white" }}>
            <Card.Body>
              <i className={`fas fa-arrow-${salesChange.isPositive ? "up" : "down"} fa-2x mb-2`}></i>
              <Card.Title>Sales This Week</Card.Title>
              <Card.Text className="fs-2 fw-bold">{dashboard_data.thisWeekSoldQuantities} L</Card.Text>
              <small className={`fw-bold ${salesChange.isPositive ? "text-success" : "text-danger"}`}>
                {salesChange.change} vs last week
              </small>
            </Card.Body>
          </Card>
        </Col>

        <Col md={2}>
          <Card className="text-center shadow-lg border-0 h-100"
            style={{ background: "linear-gradient(45deg, #8B008B, #BA55D3)", color: "white" }}>
            <Card.Body>
              <i className="fas fa-truck fa-2x mb-2"></i>
              <Card.Title>Vehicles Served Today</Card.Title>
              <Card.Text className="fs-2 fw-bold">{dashboard_data.totalServedVehiclesToday}</Card.Text>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Charts */}
      <Row className="my-4">
        <Col md={6}>
          <Card className="shadow-lg">
            <Card.Body>
              <ReactApexChart options={salesOptions} series={salesSeries} type="line" height={350} />
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card className="shadow-lg">
            <Card.Body>
              <ReactApexChart options={topStationsOptions} series={topStationsSeries} type="bar" height={350} />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default Dashboard;
